/**
 * tenantConfigStore.js
 * ─────────────────────
 * Admin-editable runtime configuration per tenant. Overrides the static
 * defaults in `src/data/tenantBranding.js`, so admins can change the logo
 * and brand colors without redeploying code.
 *
 * Persistence
 * ───────────
 * Stored in localStorage under `clearequal.tenantConfig.<tenantId>`. In
 * production this lives in the tenant config table; the API surface here
 * is identical so the swap is one file.
 *
 * Shape
 * ─────
 * {
 *   branding: { logoDataUrl, displayName, colors: {...} },
 *   sso:      { provider, entryPoint, certificate, launchEnabled, allowedDomains },
 *   integrations: {
 *     [connectorId]: { connected, lastSyncAt, fieldMap, schedule, errors }
 *   }
 * }
 */

const KEY = (tenantId) => `clearequal.tenantConfig.${tenantId}`;
const EVENT = 'clearequal.tenantConfig.changed';

const DEFAULT_CONFIG = {
  branding: {
    logoDataUrl: null,         // null = use static default from tenantBranding.js
    displayName: null,         // null = use static default
    colors: null,              // null = use static default
  },
  sso: {
    provider: null,            // 'saml' | 'oidc' | null
    entryPoint: '',            // IdP SSO URL
    certificate: '',           // IdP X.509 cert (PEM, multi-line)
    launchEnabled: false,      // when true, HCM-launched flow is permitted
    allowedDomains: [],        // email domain whitelist (e.g. ['acme.com'])
    issuer: '',                // SP entity ID (auto-generated below)
    audience: '',              // expected audience claim
  },
  approvals: {
    // Per-tenant approver for sensitive gated actions (CV uploads, identity merges).
    // 'works_council' | 'management' | 'both'
    approverRole: 'management',
    cvUploadGated: true,       // employee CV uploads require approval before they count
    identityMergeGated: true,  // uncertain cross-source merges require approval
  },
  integrations: {
    sap_successfactors: { connected: false, lastSyncAt: null, fieldMap: defaultFieldMap(), schedule: 'daily', errors: [] },
    workday:            { connected: false, lastSyncAt: null, fieldMap: defaultFieldMap(), schedule: 'daily', errors: [] },
    personio:           { connected: false, lastSyncAt: null, fieldMap: defaultFieldMap(), schedule: 'daily', errors: [] },
    bamboohr:           { connected: false, lastSyncAt: null, fieldMap: defaultFieldMap(), schedule: 'weekly', errors: [] },
    csv_upload:         { connected: true,  lastSyncAt: null, fieldMap: defaultFieldMap(), schedule: 'manual', errors: [] },
  },
};

function defaultFieldMap() {
  return [
    { sourceField: 'employee_id',     targetField: 'employees.id',           required: true,  notes: 'Stable unique key' },
    { sourceField: 'first_name',      targetField: 'employees.first_name',   required: true,  notes: '' },
    { sourceField: 'last_name',       targetField: 'employees.last_name',    required: true,  notes: '' },
    { sourceField: 'email',           targetField: 'employees.email',        required: true,  notes: 'Used for SSO matching' },
    { sourceField: 'gender',          targetField: 'employees.gender',       required: true,  notes: 'Required for Art. 9' },
    { sourceField: 'job_title',       targetField: 'employees.job_title',    required: true,  notes: '' },
    { sourceField: 'department',      targetField: 'employees.department',   required: true,  notes: '' },
    { sourceField: 'base_salary',     targetField: 'compensation.base',      required: true,  notes: 'Annual gross' },
    { sourceField: 'variable_pay',    targetField: 'compensation.variable',  required: false, notes: 'Bonus + commission' },
    { sourceField: 'hire_date',       targetField: 'employees.hire_date',    required: false, notes: '' },
    { sourceField: 'location_country',targetField: 'employees.country',      required: false, notes: 'For multi-jurisdiction' },
  ];
}

// ─── Read/Write ──────────────────────────────────────────────────────────
function readConfig(tenantId) {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(KEY(tenantId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeConfig(tenantId, config) {
  if (!tenantId) return;
  try {
    localStorage.setItem(KEY(tenantId), JSON.stringify(config));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { tenantId } }));
  } catch (_) {}
}

/**
 * Get the active config (with defaults filled in) for a tenant.
 */
export function getTenantConfig(tenantId) {
  const stored = readConfig(tenantId);
  if (!stored) return structuredClone(DEFAULT_CONFIG);
  // Deep-merge defaults onto stored to handle config-shape evolution
  return {
    branding: { ...DEFAULT_CONFIG.branding, ...(stored.branding || {}) },
    sso:      { ...DEFAULT_CONFIG.sso,      ...(stored.sso || {}) },
    approvals: { ...DEFAULT_CONFIG.approvals, ...(stored.approvals || {}) },
    integrations: { ...DEFAULT_CONFIG.integrations, ...(stored.integrations || {}) },
  };
}

/**
 * Update branding for the tenant.
 */
export function updateBranding(tenantId, partialBranding) {
  const cur = getTenantConfig(tenantId);
  cur.branding = { ...cur.branding, ...partialBranding };
  writeConfig(tenantId, cur);
  return cur.branding;
}

/**
 * Update SSO config for the tenant.
 */
export function updateSso(tenantId, partialSso) {
  const cur = getTenantConfig(tenantId);
  cur.sso = { ...cur.sso, ...partialSso };
  // Auto-derive issuer/audience based on tenant subdomain
  if (!cur.sso.issuer) {
    cur.sso.issuer = `https://${tenantId}.clearequal.com/sso`;
  }
  if (!cur.sso.audience) {
    cur.sso.audience = cur.sso.issuer;
  }
  writeConfig(tenantId, cur);
  return cur.sso;
}

/**
 * Update connector configuration.
 */
export function updateIntegration(tenantId, connectorId, partial) {
  const cur = getTenantConfig(tenantId);
  if (!cur.integrations[connectorId]) {
    cur.integrations[connectorId] = { ...DEFAULT_CONFIG.integrations.csv_upload, ...partial };
  } else {
    cur.integrations[connectorId] = { ...cur.integrations[connectorId], ...partial };
  }
  writeConfig(tenantId, cur);
  return cur.integrations[connectorId];
}

/**
 * Update the per-tenant approval configuration.
 */
export function updateApprovals(tenantId, partialApprovals) {
  const cur = getTenantConfig(tenantId);
  cur.approvals = { ...cur.approvals, ...partialApprovals };
  writeConfig(tenantId, cur);
  return cur.approvals;
}

/**
 * Convenience: read the approver role for a tenant.
 */
export function getApproverRole(tenantId) {
  return getTenantConfig(tenantId).approvals.approverRole;
}

/**
 * Subscribe to config changes. Returns unsubscribe function.
 */
export function subscribe(callback) {
  const handler = () => callback();
  window.addEventListener(EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

/**
 * Reset tenant config back to defaults (for support / debugging).
 */
export function resetTenantConfig(tenantId) {
  try { localStorage.removeItem(KEY(tenantId)); } catch (_) {}
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { tenantId } }));
}
